import { useState, useEffect } from 'react'
import { Transition } from '@headlessui/react'
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa'

export default function Carousel({
    items = [],
    autoPlay = false,
    interval = 3000,
    size = 'medium',
}) {
    const [current, setCurrent] = useState(0)
    const total = items.length

    const next = () => setCurrent(prev => (prev + 1) % total)
    const prev = () => setCurrent(prev => (prev - 1 + total) % total)

    const heightClass =
        {
            screen: 'h-[60vh] sm:h-screen',
            large: 'h-64 sm:h-96',
            medium: 'h-48 sm:h-64 md:h-80',
        }[size] || 'h-48 sm:h-64'

    useEffect(() => {
        if (!autoPlay || total <= 1) return
        const timer = setInterval(next, interval)
        return () => clearInterval(timer)
    }, [current, autoPlay, total])

    // No items check
    if (!items || items.length === 0) {
        return (
            <div className={`${heightClass} bg-gray-100 flex items-center justify-center`}>
                No items
            </div>
        )
    }

    return (
        <div className='relative w-full max-w-full overflow-hidden'>
            {/* Carousel Container - FIXED VERSION */}
            <div className={`relative ${heightClass} overflow-hidden`}>
                {items.map((item, index) => {
                    const isActive = index === current

                    return (
                        <Transition
                            key={index}
                            show={isActive}
                            enter='transition-all duration-700 ease-out'
                            enterFrom='opacity-0 translate-x-10'
                            enterTo='opacity-100 translate-x-0'
                            leave='transition-all duration-700 ease-in'
                            leaveFrom='opacity-100 translate-x-0'
                            leaveTo='opacity-0 -translate-x-10'
                            // CRITICAL FIX: Use conditional classes
                            className={`absolute inset-0 transition-all duration-700 ${
                                isActive ? 'z-20' : 'z-10'
                            }`}
                            // Remove the slide from layout when not active
                            style={{
                                pointerEvents: isActive ? 'auto' : 'none',
                                // Ensure proper stacking
                                transform: isActive ? 'translateX(0)' : 'translateX(100%)',
                            }}
                            // After leave animation completes, ensure it's hidden
                            afterLeave={() => {
                                // Force cleanup if needed
                            }}
                        >
                            <div className='flex items-center justify-center w-full h-full'>
                                {item}
                            </div>
                        </Transition>
                    )
                })}
            </div>

            {/* Navigation Buttons - Increased z-index */}
            {total > 1 && (
                <>
                    <button
                        onClick={prev}
                        className='absolute z-40 p-2 transform -translate-y-1/2 bg-white rounded-full shadow-lg sm:p-3 left-2 top-1/2 bg-opacity-60 hover:bg-opacity-80'
                        aria-label='Previous slide'
                    >
                        <FaChevronLeft className='text-lg text-black sm:text-xl' />
                    </button>

                    <button
                        onClick={next}
                        className='absolute z-40 p-2 transform -translate-y-1/2 bg-white rounded-full shadow-lg sm:p-3 right-2 top-1/2 bg-opacity-60 hover:bg-opacity-80'
                        aria-label='Next slide'
                    >
                        <FaChevronRight className='text-lg text-black sm:text-xl' />
                    </button>

                    {/* Dots - Increased z-index and fixed pointer events */}
                    <div className='absolute z-40 flex gap-2 transform -translate-x-1/2 sm:gap-4 bottom-3 left-1/2'>
                        {items.map((_, index) => (
                            <button
                                key={index}
                                onClick={() => setCurrent(index)}
                                className={`h-2.5 w-2.5 sm:h-4 sm:w-4 rounded-full transition-colors duration-300 ${
                                    index === current
                                        ? 'bg-yellow-500 scale-110'
                                        : 'bg-gray-400 hover:bg-gray-300'
                                }`}
                                aria-label={`Go to slide ${index + 1}`}
                            />
                        ))}
                    </div>
                </>
            )}
        </div>
    )
}
